import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { SearchableSelect } from '../../components/ui/searchable-select';
import {
  ArrowDownLeft, ArrowUpRight, RefreshCw, History, FileText, Upload, Search,
  MoreVertical, Download, Receipt, Smartphone, Trash2,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_PILL = {
  Active:    'bg-green-50 text-green-700 border-green-300',
  Inactive:  'bg-amber-50 text-amber-700 border-amber-300',
  'In Store':'bg-slate-100 text-slate-700 border-slate-300',
};
// Per user spec: surface "In-Use" / "Available" labels (DB values stay as-is)
const STATUS_LABEL = { Active: 'In-Use', Inactive: 'Available', 'In Store': 'In Store' };

const REASON_PRESETS = [
  'Rider going on Vacation',
  'Change of platform',
  'Others',
];

const nowParts = () => {
  const t = new Date();
  return {
    date: `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`,
    time: `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`,
  };
};

const fmtFileURL = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API.replace(/\/api$/, '')}${url}`;
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SimStatusLog = () => {
  const [sims, setSims] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [riders, setRiders] = useState([]);
  const [staff, setStaff] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [takeoverOpen, setTakeoverOpen] = useState(false);
  const [uploadBillSim, setUploadBillSim] = useState(null); // {id, mobile_number}
  const [billsModalSim, setBillsModalSim] = useState(null);
  const [historyModalSim, setHistoryModalSim] = useState(null);

  // ----- Fetch initial data ---------------------------------------------------
  const fetchAll = async () => {
    try {
      setLoading(true);
      const [simsRes, txnRes, ridersRes, staffRes, usersRes] = await Promise.all([
        axios.get(`${API}/inventory/sim-cards`, { headers: getAuthHeader() }),
        axios.get(`${API}/inventory/sim-cards/transactions/all`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/talent/employees?employee_type=staff`, { headers: getAuthHeader() }),
        axios.get(`${API}/users/names`, { headers: getAuthHeader() }),
      ]);
      setSims(simsRes.data?.items || simsRes.data || []);
      setTransactions(txnRes.data || []);
      setRiders(ridersRes.data?.riders || ridersRes.data || []);
      setStaff((staffRes.data?.employees || staffRes.data || []).filter(e =>
        (e.employee_type || '').toLowerCase() === 'staff' || (e.type || '').toLowerCase() === 'staff'
      ));
      setUsers(usersRes.data?.users || usersRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load SIM data');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  // ----- Filtered SIMs for the log table -------------------------------------
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sims.filter(s => {
      if (statusFilter !== 'all' && (s.status || 'In Store') !== statusFilter) return false;
      if (!q) return true;
      return [s.mobile_number, s.rider_name, s.rider_unique_id, s.platform]
        .some(v => String(v || '').toLowerCase().includes(q));
    });
  }, [sims, search, statusFilter]);

  return (
    <div className="space-y-4">
      {/* Header strip — handover/takeover buttons */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Button
            onClick={() => setHandoverOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            data-testid="open-handover-form-btn"
          >
            <ArrowDownLeft className="w-4 h-4" />
            Handover SIM
          </Button>
          <Button
            onClick={() => setTakeoverOpen(true)}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-2"
            data-testid="open-takeover-form-btn"
          >
            <ArrowUpRight className="w-4 h-4" />
            Takeover SIM
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <Input
                placeholder="Search mobile, rider, platform…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-64"
                data-testid="sim-log-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">In-Use</SelectItem>
                <SelectItem value="Inactive">Available</SelectItem>
                <SelectItem value="In Store">In Store</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="ghost" onClick={fetchAll} title="Refresh" data-testid="sim-log-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Log table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-blue-600" />
            SIM Status Log ({rows.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-auto">
          <Table className="min-w-[1200px]">
            <TableHeader>
              <TableRow className="text-[11px] uppercase tracking-wide text-slate-600">
                <TableHead>Mobile no.</TableHead>
                <TableHead>Rider Assigned</TableHead>
                <TableHead>Rider ID</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead>Platform ID</TableHead>
                <TableHead>Date of Handover</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Upload Bills</TableHead>
                <TableHead className="text-center w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">
                  No SIM cards found. Add one from the Inventory tab to begin.
                </TableCell></TableRow>
              ) : rows.map((sim) => {
                const status = sim.status || 'In Store';
                return (
                  <TableRow key={sim.id} className="text-sm" data-testid={`sim-log-row-${sim.id}`}>
                    <TableCell className="font-mono font-medium">{sim.mobile_number}</TableCell>
                    <TableCell>{sim.rider_name || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="font-mono text-xs">{sim.rider_unique_id || '—'}</TableCell>
                    <TableCell>{sim.platform || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{sim.platform_id || sim.rider_platform_id || '—'}</TableCell>
                    <TableCell className="text-xs">{sim.date_of_handover || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_PILL[status]}>{STATUS_LABEL[status] || status}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm" variant="outline"
                        className="h-7 px-2 text-xs gap-1"
                        onClick={() => setUploadBillSim(sim)}
                        data-testid={`upload-bill-btn-${sim.id}`}
                      >
                        <Upload className="w-3 h-3" /> Upload
                      </Button>
                    </TableCell>
                    <TableCell className="text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`sim-row-actions-${sim.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => setHistoryModalSim(sim)} data-testid={`view-assignments-${sim.id}`}>
                            <History className="w-3.5 h-3.5 mr-2" /> View Assignments
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setBillsModalSim(sim)} data-testid={`view-bills-${sim.id}`}>
                            <Receipt className="w-3.5 h-3.5 mr-2" /> View Bills
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <HandoverDialog
        open={handoverOpen}
        onOpenChange={setHandoverOpen}
        sims={sims.filter(s => (s.status || 'In Store') !== 'Active')}
        riders={riders}
        staff={staff}
        users={users}
        onDone={fetchAll}
      />
      <TakeoverDialog
        open={takeoverOpen}
        onOpenChange={setTakeoverOpen}
        sims={sims.filter(s => s.status === 'Active')}
        users={users}
        onDone={fetchAll}
      />
      <UploadBillDialog
        sim={uploadBillSim}
        onOpenChange={(o) => { if (!o) setUploadBillSim(null); }}
        onDone={fetchAll}
      />
      <ViewAssignmentsDialog
        sim={historyModalSim}
        transactions={transactions.filter(t => t.sim_card_id === historyModalSim?.id)}
        onOpenChange={(o) => { if (!o) setHistoryModalSim(null); }}
      />
      <ViewBillsDialog
        sim={billsModalSim}
        onOpenChange={(o) => { if (!o) setBillsModalSim(null); }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Handover dialog
// ---------------------------------------------------------------------------

function HandoverDialog({ open, onOpenChange, sims, riders, staff, users, onDone }) {
  const [simId, setSimId] = useState('');
  const [assigneeType, setAssigneeType] = useState('rider'); // 'rider' | 'staff'
  const [assigneeId, setAssigneeId] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [picId, setPicId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const np = nowParts();
      setSimId(''); setAssigneeType('rider'); setAssigneeId('');
      setDate(np.date); setTime(np.time); setPicId('');
    }
  }, [open]);

  const simOptions = useMemo(() => sims.map(s => ({
    value: s.id,
    label: `${s.mobile_number}${s.service_provider ? ` (${s.service_provider})` : ''}`,
    sublabel: s.status ? `Status: ${STATUS_LABEL[s.status] || s.status}` : null,
  })), [sims]);

  const assigneeOptions = useMemo(() => {
    const list = assigneeType === 'rider' ? riders : staff;
    return list.map(p => ({
      value: p.id,
      label: p.name || p.full_name || '—',
      sublabel: p.rider_unique_id || p.employee_unique_id || p.unique_id || '',
    }));
  }, [assigneeType, riders, staff]);

  // Auto-fill on assignee selection
  const selectedAssignee = useMemo(() => {
    if (!assigneeId) return null;
    const list = assigneeType === 'rider' ? riders : staff;
    return list.find(p => p.id === assigneeId) || null;
  }, [assigneeId, assigneeType, riders, staff]);

  const userOptions = useMemo(() => users.map(u => ({ value: u.id, label: u.name || u.email })), [users]);

  const handleSubmit = async () => {
    if (!simId || !assigneeId || !date || !time) {
      toast.error('Please fill SIM, assignee, date and time.');
      return;
    }
    try {
      setSubmitting(true);
      const pic = users.find(u => u.id === picId);
      const body = {
        assignee_type: assigneeType,
        rider_id: assigneeId,                     // accepted for both rider AND staff
        transaction_date: date,
        transaction_time: time,
        reason: 'Handover',
        person_in_charge_id: picId || null,
        person_in_charge_name: pic?.name || pic?.email || null,
        platform: selectedAssignee?.primary_platform || selectedAssignee?.platform || null,
      };
      await axios.post(`${API}/inventory/sim-cards/${simId}/handover`, body, { headers: getAuthHeader() });
      toast.success('SIM handed over');
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Handover failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="handover-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowDownLeft className="w-5 h-5 text-blue-600" /> Handover SIM Card</DialogTitle>
          <DialogDescription>Assign a SIM card to a Rider or Staff member. The SIM status becomes <strong>In-Use</strong>.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="col-span-2">
            <Label className="text-xs">Mobile no. (SIM Card) <span className="text-red-500">*</span></Label>
            <SearchableSelect
              value={simId}
              onValueChange={setSimId}
              options={simOptions}
              placeholder="Search mobile number…"
              data-testid="handover-sim-select"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Assign To <span className="text-red-500">*</span></Label>
            <div className="flex gap-2 mb-2">
              {['rider', 'staff'].map(t => (
                <Button
                  key={t}
                  type="button"
                  size="sm"
                  variant={assigneeType === t ? 'default' : 'outline'}
                  onClick={() => { setAssigneeType(t); setAssigneeId(''); }}
                  className={assigneeType === t ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  data-testid={`handover-assignee-type-${t}`}
                >
                  {t === 'rider' ? 'Rider' : 'Staff'}
                </Button>
              ))}
            </div>
            <SearchableSelect
              value={assigneeId}
              onValueChange={setAssigneeId}
              options={assigneeOptions}
              placeholder={`Search ${assigneeType} by name or ID…`}
              data-testid="handover-assignee-select"
            />
          </div>

          {/* Auto-filled assignee details */}
          {selectedAssignee && (
            <Card className="col-span-2 bg-slate-50/60 border-slate-200">
              <CardContent className="p-3 text-xs grid grid-cols-2 gap-y-1.5 gap-x-4">
                <div><span className="text-slate-500">Name:</span> <span className="font-medium">{selectedAssignee.name || selectedAssignee.full_name || '—'}</span></div>
                <div><span className="text-slate-500">{assigneeType === 'rider' ? 'Rider ID' : 'Employee ID'}:</span> <span className="font-mono">{selectedAssignee.rider_unique_id || selectedAssignee.employee_unique_id || selectedAssignee.unique_id || '—'}</span></div>
                <div><span className="text-slate-500">Emirates ID:</span> <span className="font-mono">{selectedAssignee.emirates_id || selectedAssignee.eid || '—'}</span></div>
                <div><span className="text-slate-500">Platform:</span> {selectedAssignee.primary_platform || selectedAssignee.platform || '—'}</div>
                <div><span className="text-slate-500">Platform ID:</span> <span className="font-mono">{selectedAssignee.primary_platform_id || selectedAssignee.platform_id || '—'}</span></div>
                <div><span className="text-slate-500">Bike Plate:</span> <span className="font-mono">{selectedAssignee.bike_plate || selectedAssignee.plate || '—'}</span></div>
              </CardContent>
            </Card>
          )}

          <div>
            <Label className="text-xs">Date of Handover <span className="text-red-500">*</span></Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="handover-date" />
          </div>
          <div>
            <Label className="text-xs">Time of Handover <span className="text-red-500">*</span></Label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="handover-time" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Person In Charge</Label>
            <SearchableSelect
              value={picId}
              onValueChange={setPicId}
              options={userOptions}
              placeholder="Select user…"
              data-testid="handover-pic-select"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="handover-submit-btn">
            <ArrowDownLeft className="w-4 h-4" /> {submitting ? 'Handing over…' : 'Handover SIM'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Takeover dialog
// ---------------------------------------------------------------------------

function TakeoverDialog({ open, onOpenChange, sims, users, onDone }) {
  const [simId, setSimId] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [picId, setPicId] = useState('');
  const [reasonType, setReasonType] = useState('Rider going on Vacation');
  const [otherReason, setOtherReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const np = nowParts();
      setSimId(''); setDate(np.date); setTime(np.time);
      setPicId(''); setReasonType('Rider going on Vacation'); setOtherReason('');
    }
  }, [open]);

  const simOptions = useMemo(() => sims.map(s => ({
    value: s.id,
    label: `${s.mobile_number}${s.rider_name ? ` — ${s.rider_name}` : ''}`,
    sublabel: s.platform || null,
  })), [sims]);

  const selectedSim = useMemo(() => sims.find(s => s.id === simId), [sims, simId]);
  const userOptions = useMemo(() => users.map(u => ({ value: u.id, label: u.name || u.email })), [users]);

  const handleSubmit = async () => {
    const reason = reasonType === 'Others' ? otherReason.trim() : reasonType;
    if (!simId || !date || !time || !reason) {
      toast.error('Please fill SIM, date, time, and reason.');
      return;
    }
    try {
      setSubmitting(true);
      const pic = users.find(u => u.id === picId);
      await axios.post(`${API}/inventory/sim-cards/${simId}/takeover`, {
        transaction_date: date,
        transaction_time: time,
        reason,
        person_in_charge_id: picId || null,
        person_in_charge_name: pic?.name || pic?.email || null,
      }, { headers: getAuthHeader() });
      toast.success('SIM taken back. Status → Available.');
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Takeover failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="takeover-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowUpRight className="w-5 h-5 text-amber-600" /> Takeover SIM Card</DialogTitle>
          <DialogDescription>Take a SIM back from a Rider / Staff member. The SIM status becomes <strong>Available</strong>.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="col-span-2">
            <Label className="text-xs">Mobile no. (currently In-Use) <span className="text-red-500">*</span></Label>
            <SearchableSelect
              value={simId}
              onValueChange={setSimId}
              options={simOptions}
              placeholder="Search mobile number…"
              data-testid="takeover-sim-select"
            />
          </div>

          {selectedSim && (
            <Card className="col-span-2 bg-slate-50/60 border-slate-200">
              <CardContent className="p-3 text-xs grid grid-cols-2 gap-y-1.5 gap-x-4">
                <div><span className="text-slate-500">Currently held by:</span> <span className="font-medium">{selectedSim.rider_name || '—'}</span></div>
                <div><span className="text-slate-500">Rider ID:</span> <span className="font-mono">{selectedSim.rider_unique_id || '—'}</span></div>
                <div><span className="text-slate-500">Platform:</span> {selectedSim.platform || '—'}</div>
                <div><span className="text-slate-500">Since:</span> {selectedSim.date_of_handover || '—'}</div>
              </CardContent>
            </Card>
          )}

          <div>
            <Label className="text-xs">Date of Takeover <span className="text-red-500">*</span></Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="takeover-date" />
          </div>
          <div>
            <Label className="text-xs">Time of Takeover <span className="text-red-500">*</span></Label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="takeover-time" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Person In Charge</Label>
            <SearchableSelect
              value={picId}
              onValueChange={setPicId}
              options={userOptions}
              placeholder="Select user…"
              data-testid="takeover-pic-select"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Reason for Takeover <span className="text-red-500">*</span></Label>
            <Select value={reasonType} onValueChange={setReasonType}>
              <SelectTrigger data-testid="takeover-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASON_PRESETS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            {reasonType === 'Others' && (
              <Textarea
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Describe the reason…"
                className="mt-2"
                data-testid="takeover-other-reason"
              />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700 gap-2" data-testid="takeover-submit-btn">
            <ArrowUpRight className="w-4 h-4" /> {submitting ? 'Taking back…' : 'Takeover SIM'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Upload Bill dialog
// ---------------------------------------------------------------------------

function UploadBillDialog({ sim, onOpenChange, onDone }) {
  const [billingMonth, setBillingMonth] = useState('');
  const [billDate, setBillDate] = useState('');
  const [billAmount, setBillAmount] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (sim) {
      const t = new Date();
      setBillingMonth(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`);
      setBillDate(nowParts().date);
      setBillAmount(''); setFile(null);
    }
  }, [sim]);

  const handleSubmit = async () => {
    if (!sim || !billingMonth || !billDate || !billAmount || !file) {
      toast.error('All fields are required.');
      return;
    }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('billing_month', billingMonth);
      fd.append('bill_date', billDate);
      fd.append('bill_amount', billAmount);
      fd.append('file', file);
      await axios.post(`${API}/inventory/sim-cards/${sim.id}/bills`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Bill uploaded');
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!sim} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="upload-bill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-blue-600" /> Upload Monthly Bill</DialogTitle>
          <DialogDescription>For SIM <strong className="font-mono">{sim?.mobile_number}</strong></DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div>
            <Label className="text-xs">Billing Month <span className="text-red-500">*</span></Label>
            <Input type="month" value={billingMonth} onChange={(e) => setBillingMonth(e.target.value)} data-testid="bill-month-input" />
          </div>
          <div>
            <Label className="text-xs">Date of Bill <span className="text-red-500">*</span></Label>
            <Input type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)} data-testid="bill-date-input" />
          </div>
          <div>
            <Label className="text-xs">Bill Amount (AED) <span className="text-red-500">*</span></Label>
            <Input type="number" step="0.01" value={billAmount} onChange={(e) => setBillAmount(e.target.value)} placeholder="0.00" data-testid="bill-amount-input" />
          </div>
          <div>
            <Label className="text-xs">PDF File <span className="text-red-500">*</span></Label>
            <Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="bill-file-input" />
            {file && <p className="text-[11px] text-slate-500 mt-1">{file.name} • {(file.size / 1024).toFixed(1)} KB</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="bill-submit-btn">
            <Upload className="w-4 h-4" /> {submitting ? 'Uploading…' : 'Upload Bill'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// View Assignments (history) modal
// ---------------------------------------------------------------------------

function ViewAssignmentsDialog({ sim, transactions, onOpenChange }) {
  return (
    <Dialog open={!!sim} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="view-assignments-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-blue-600" /> Assignment History</DialogTitle>
          <DialogDescription>SIM <strong className="font-mono">{sim?.mobile_number}</strong> — full handover/takeover history</DialogDescription>
        </DialogHeader>
        <div className="overflow-auto max-h-[70vh]">
          <Table>
            <TableHeader>
              <TableRow className="text-[11px] uppercase text-slate-600">
                <TableHead>Date</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Rider/Staff</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Person In Charge</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No history yet.</TableCell></TableRow>
              ) : transactions.map(t => (
                <TableRow key={t.id} className="text-xs">
                  <TableCell>{t.transaction_date}</TableCell>
                  <TableCell className="font-mono">{t.transaction_time}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      t.transaction_type === 'Handover' ? 'bg-blue-50 text-blue-700 border-blue-300' :
                      t.transaction_type === 'Takeover' ? 'bg-amber-50 text-amber-700 border-amber-300' :
                      'bg-slate-100 text-slate-700'}>
                      {t.transaction_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{t.rider_name || '—'}</p>
                    <p className="text-[10px] text-slate-500 font-mono">{t.rider_unique_id || ''}{t.assignee_type ? ` · ${t.assignee_type}` : ''}</p>
                  </TableCell>
                  <TableCell>{t.platform || '—'}</TableCell>
                  <TableCell className="max-w-[200px] truncate" title={t.reason}>{t.reason}</TableCell>
                  <TableCell>{t.person_in_charge_name || t.created_by || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// View Bills modal
// ---------------------------------------------------------------------------

function ViewBillsDialog({ sim, onOpenChange }) {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sim) return;
    setLoading(true);
    axios.get(`${API}/inventory/sim-cards/${sim.id}/bills`, { headers: getAuthHeader() })
      .then(res => setBills(res.data || []))
      .catch(e => toast.error(e.response?.data?.detail || 'Failed to load bills'))
      .finally(() => setLoading(false));
  }, [sim]);

  const handleDelete = async (billId) => {
    if (!window.confirm('Delete this bill record?')) return;
    try {
      await axios.delete(`${API}/inventory/sim-cards/bills/${billId}`, { headers: getAuthHeader() });
      setBills(prev => prev.filter(b => b.id !== billId));
      toast.success('Bill deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Dialog open={!!sim} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="view-bills-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-blue-600" /> SIM Bills</DialogTitle>
          <DialogDescription>SIM <strong className="font-mono">{sim?.mobile_number}</strong> — monthly bills</DialogDescription>
        </DialogHeader>
        <div className="overflow-auto max-h-[70vh]">
          <Table>
            <TableHeader>
              <TableRow className="text-[11px] uppercase text-slate-600">
                <TableHead>Billing Month</TableHead>
                <TableHead>Bill Date</TableHead>
                <TableHead className="text-right">Amount (AED)</TableHead>
                <TableHead>File</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">Loading…</TableCell></TableRow>
              ) : bills.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">No bills uploaded yet.</TableCell></TableRow>
              ) : bills.map(b => (
                <TableRow key={b.id} className="text-xs">
                  <TableCell className="font-medium">{b.billing_month}</TableCell>
                  <TableCell>{b.bill_date}</TableCell>
                  <TableCell className="text-right font-mono">{Number(b.bill_amount).toFixed(2)}</TableCell>
                  <TableCell className="text-slate-600 truncate max-w-[180px]" title={b.bill_filename}>{b.bill_filename || '—'}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      {b.bill_url && (
                        <a
                          href={fmtFileURL(b.bill_url)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-[11px] px-2 py-1 border border-blue-200 rounded hover:bg-blue-50"
                          data-testid={`download-bill-${b.id}`}
                        >
                          <Download className="w-3 h-3" /> Download
                        </a>
                      )}
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => handleDelete(b.id)} title="Delete">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SimStatusLog;
